import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Stack, Typography } from '@mui/material';
import { useRetention } from '../RetentionContext';
import StageTabs from '../components/StageTabs';
import DemoAttemptStage from '../stages/DemoAttemptStage';
import GoLiveStage from '../stages/GoLiveStage';
import PhaseHeader from '../components/PhaseHeader';
import { EmptyState, IconBadge, SectionCard, SoftChip } from '../components/ui';
import { MAPPING_STATUS } from '../constants/stages';
import { RT } from '../theme/retentionTheme';
import { getDemo } from '../../../redux/features/retentionSlice';

const MAPPING_COLOR = { 0: RT.faint, 1: RT.warning, 2: RT.success, 3: RT.error };

// Phase 2 — stages 5–7 (§2.3): demo (DemoAttemptStage 5), both feedback halves + remap (6), Admission Confirmed /
// go-live checklist (GoLiveStage 7). All read GET /retention/:id/demo; the side card is the attempt history.
export default function Demo() {
  const { courseEnrollmentId, currentStage, progressByStage } = useRetention();
  const dispatch = useDispatch();
  const { demo } = useSelector((state) => state.retention);

  useEffect(() => {
    dispatch(getDemo(courseEnrollmentId));
  }, [dispatch, courseEnrollmentId]);

  const attempts = demo.data?.attempts || [];

  return (
    <Stack gap={2.5}>
      <PhaseHeader phase={2} currentStage={currentStage} />
      <StageTabs
        phase={2}
        currentStage={currentStage}
        progressByStage={progressByStage}
        renderStage={(no) => (no === 7 ? <GoLiveStage /> : <DemoAttemptStage stageNo={no} />)}
        aside={
          <SectionCard icon="solar:videocamera-record-bold-duotone" color="#7C3AED" title="Demo attempts">
            {attempts.length === 0 ? (
              <EmptyState
                icon={demo.loading ? 'svg-spinners:ring-resize' : 'solar:videocamera-add-linear'}
                title={demo.loading ? 'Loading…' : 'No attempts yet'}
                compact
              />
            ) : (
              <Stack gap={1.25}>
                {attempts.map((a) => (
                  <Stack
                    key={a.retention_demo_attempt_id}
                    direction="row"
                    alignItems="center"
                    gap={1.25}
                    sx={{ p: 1.25, borderRadius: 2.5, border: `1px solid ${RT.line}` }}
                  >
                    <IconBadge icon="solar:user-speak-bold-duotone" color={MAPPING_COLOR[a.mapping_status]} size={36} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>
                        {a.trainer_name || (Number(a.mapping_status) === 0 ? 'Awaiting tutor' : '—')}
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ color: RT.muted }} noWrap>
                        {a.subject_name} · attempt {a.attempt_no}
                      </Typography>
                      {(a.rejection_reason || a.tutor_response_reason) && (
                        <Typography variant="caption" component="div" sx={{ color: RT.error }} noWrap>
                          {a.rejection_reason ? `Parent: ${a.rejection_reason}` : `Tutor declined: ${a.tutor_response_reason}`}
                        </Typography>
                      )}
                    </Box>
                    <SoftChip label={MAPPING_STATUS[a.mapping_status]?.label} color={MAPPING_COLOR[a.mapping_status]} />
                  </Stack>
                ))}
              </Stack>
            )}
          </SectionCard>
        }
      />
    </Stack>
  );
}
